import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Product } from '@/mocks/products';
import { useFavorites } from '@/hooks/useFavorites';
import { useAuth, applyMargin } from '@/hooks/useAuth';

interface ProductCardProps {
  product: Product;
  onInquiry: (product: Product) => void;
  compact?: boolean;
}

const badgeColors: Record<string, { bg: string; text: string }> = {
  '추천': { bg: '#2E5240', text: '#fff' },
  '최저가': { bg: '#B84040', text: '#fff' },
  '인기': { bg: '#5C4F42', text: '#fff' },
  '프리미엄': { bg: '#C4985A', text: '#fff' },
};

export default function ProductCard({ product, onInquiry, compact = false }: ProductCardProps) {
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { isLoggedIn, role } = useAuth();

  const badge = product.badge ? (badgeColors[product.badge] || { bg: '#2E5240', text: '#fff' }) : null;
  const favorited = isFavorite(product.id);

  const boxUnit = ['스킨케어·뷰티', '섬유향수', '생활용품'].includes(product.category) ? '개' : '봉';

  // 역할별 가격 레이블
  const priceLabel = role === 'admin' ? '원가' : '파트너가';
  const boxPriceLabel = role === 'admin' ? '박스원가' : '파트너 박스가';

  return (
    <div
      onClick={() => navigate(`/product/${product.id}`)}
      className="group rounded-xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
      style={{ backgroundColor: '#fff', border: '1px solid #E5DDD0' }}
    >
      {/* ── 이미지 영역 ── */}
      <div className="relative overflow-hidden" style={{ paddingBottom: '72%', backgroundColor: '#F8F4EE' }}>
        {!imgError && product.image ? (
          <img
            src={product.image}
            alt={product.name}
            onError={() => setImgError(true)}
            className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="w-12 h-12 flex items-center justify-center">
              <i className="ri-image-add-line text-4xl" style={{ color: '#D8CEBF' }}></i>
            </div>
            <span className="text-[10px] tracking-wide" style={{ color: '#D8CEBF' }}>사진 준비중</span>
          </div>
        )}

        {/* 배지 */}
        {badge && product.badge && (
          <span
            className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-md tracking-wide whitespace-nowrap"
            style={{ backgroundColor: badge.bg, color: badge.text }}
          >
            {product.badge}
            <span className="opacity-70 ml-1 font-normal hidden sm:inline">/ {product.badgeZh}</span>
          </span>
        )}

        {/* 카테고리 태그 */}
        <span
          className="absolute top-2 right-2 text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-md tracking-wide whitespace-nowrap"
          style={{ backgroundColor: 'rgba(248,244,238,0.95)', color: '#8C7D6E', border: '1px solid #D8CEBF' }}
        >
          {product.category}
        </span>

        {/* 관심 버튼 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite(product.id);
          }}
          className={`absolute bottom-2 right-2 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
            favorited ? 'scale-105' : 'opacity-0 group-hover:opacity-100'
          }`}
          style={
            favorited
              ? { backgroundColor: '#C4985A', color: '#fff' }
              : { backgroundColor: 'rgba(248,244,238,0.95)', color: '#8C7D6E', border: '1px solid #D8CEBF' }
          }
          aria-label={favorited ? '관심 상품 해제' : '관심 상품 추가'}
        >
          <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} text-xs sm:text-sm`}></i>
        </button>
      </div>

      {/* ── 정보 영역 ── */}
      <div className="flex flex-col flex-1 p-2.5 sm:p-3 md:p-4">

        {/* 코드 */}
        <p className="text-[10px] sm:text-[11px] font-mono tracking-wider mb-0.5" style={{ color: '#8C7D6E' }}>
          {product.code}
        </p>

        {/* 상품명 ZH - 주 */}
        <h3
          className={`font-black leading-snug tracking-tight ${
            compact ? 'text-[13px] sm:text-[14px]' : 'text-[13px] sm:text-[15px] md:text-[16px]'
          }`}
          style={{ color: '#2A2118' }}
        >
          {product.nameZh}
        </h3>

        {/* 상품명 KO - 부 */}
        <p
          className={`font-semibold mb-2 tracking-wide leading-snug ${
            compact ? 'text-[10px] sm:text-[11px]' : 'text-[10px] sm:text-[12px]'
          }`}
          style={{ color: '#7A6E64' }}
        >
          {product.name}
        </p>

        {/* 규격 */}
        <div className="mb-2 pb-2" style={{ borderBottom: '1px solid #E5DDD0' }}>
          <p className="text-[11px] leading-snug" style={{ color: '#5C4F42' }}>{product.specificationZh}</p>
          <p className="text-[10px] leading-snug font-medium" style={{ color: '#7A6E64' }}>{product.specification}</p>
        </div>

        {/* ── 가격 영역 ── */}
        <div className="space-y-1 flex-1">
          {isLoggedIn ? (
            <>
              {/* 소비자가격 */}
              {product.retailPrice && product.retailPrice > 0 && (
                <div className="flex items-start justify-between gap-1">
                  <span className="text-[10px] leading-tight font-semibold shrink-0 pt-0.5" style={{ color: '#7A6E64' }}>소비자가</span>
                  <div className="text-right">
                    <span className="text-[11px] font-semibold tracking-tight block" style={{ color: '#5C4F42' }}>₩{product.retailPrice.toLocaleString()}</span>
                    <span className="text-[10px]" style={{ color: '#8C7D6E' }}>≈ {Math.round(product.retailPrice / 216)}元</span>
                  </div>
                </div>
              )}
              {/* 공급단가 (역할별 레이블) */}
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] leading-tight font-semibold shrink-0" style={{ color: '#7A6E64' }}>{priceLabel}</span>
                {product.unitPrice > 0 ? (
                  <span className="text-[13px] sm:text-[14px] font-bold tracking-tight" style={{ color: '#C4985A' }}>
                    {applyMargin(product.unitPrice, role ?? 'admin')} 元
                  </span>
                ) : (
                  <span className="text-[10px] sm:text-[11px] font-bold tracking-tight px-1.5 py-0.5 rounded-md whitespace-nowrap" style={{ color: '#2E5240', backgroundColor: '#E8F0EC' }}>문의/询价</span>
                )}
              </div>
              {/* 박스 정보 */}
              {product.boxQuantity > 0 && (
                <>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] leading-tight font-semibold shrink-0" style={{ color: '#7A6E64' }}>박스입수</span>
                    <span className="text-[12px] sm:text-[13px] font-semibold" style={{ color: '#5C4F42' }}>{product.boxQuantity}{boxUnit}</span>
                  </div>
                  <div className="flex items-center justify-between rounded-lg px-2 py-1.5 mt-0.5" style={{ backgroundColor: '#F0EAE0' }}>
                    <span className="text-[10px] font-bold leading-tight" style={{ color: '#5C4F42' }}>{boxPriceLabel}</span>
                    {product.boxPrice > 0 ? (
                      <span className={`font-black tracking-tight ${compact ? 'text-[14px]' : 'text-[15px] sm:text-[16px] md:text-[17px]'}`} style={{ color: '#2E5240' }}>
                        {applyMargin(product.boxPrice, role ?? 'admin')} 元
                      </span>
                    ) : (
                      <span className="text-[12px] font-bold tracking-tight" style={{ color: '#2E5240' }}>문의/询价</span>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            /* 비로그인: 가격 잠금 표시 */
            <div className="flex flex-col items-center justify-center py-2 rounded-xl gap-1" style={{ backgroundColor: '#F7F4F0', border: '1px dashed #D8CEBF' }}>
              <i className="ri-lock-line text-base" style={{ color: '#B8A898' }}></i>
              <p className="text-[10px] font-semibold text-center leading-snug" style={{ color: '#8C7D6E' }}>登录后查看价格</p>
              <p className="text-[9px] text-center tracking-wide" style={{ color: '#A09080' }}>거래처 로그인 후 단가 확인</p>
            </div>
          )}
        </div>

        {/* ── 메타 정보 ── */}
        <div className="mt-2 space-y-1 pt-2" style={{ borderTop: '1px solid #E5DDD0' }}>
          <div className="flex items-start gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-time-line text-[11px]" style={{ color: '#B8A898' }}></i>
            </div>
            <span className="text-[10px] leading-snug break-keep" style={{ color: '#8C7D6E' }}>
              {product.expiryInfoZh}
              <span className="font-medium" style={{ color: '#9A8E84' }}> / {product.expiryInfo}</span>
            </span>
          </div>
          <div className="flex items-start gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-temp-cold-line text-[11px]" style={{ color: '#B8A898' }}></i>
            </div>
            <span className="text-[10px] leading-snug break-keep" style={{ color: '#8C7D6E' }}>
              {product.storageMethodZh}
              <span className="font-medium" style={{ color: '#9A8E84' }}> / {product.storageMethod}</span>
            </span>
          </div>
        </div>

        {/* ── 문의 버튼 ── */}
        <button
          onClick={(e) => { e.stopPropagation(); onInquiry(product); }}
          className="mt-2.5 w-full py-2 sm:py-2.5 text-[12px] sm:text-[13px] font-bold rounded-lg transition-all duration-200 whitespace-nowrap tracking-wide"
          style={{ border: '1.5px solid #1B2B3A', color: '#1B2B3A', backgroundColor: 'transparent' }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1B2B3A';
            (e.currentTarget as HTMLButtonElement).style.color = '#fff';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = '#1B2B3A';
          }}
        >
          询价 <span className="font-semibold opacity-70">/ 문의하기</span>
        </button>
      </div>
    </div>
  );
}
